import { useState, useRef, useCallback, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Input from "../../input";
import Select from "../../select";
import { toast } from "sonner";
import { TrashIcon, Edit2, X, Check, Loader2, Sparkles } from "lucide-react";
import { SYNOPSIS_MODEL } from "../../../views/crm-pages/ticket/types";
import { RaiseTicketModalProps, TeamDetails, TicketFormData } from "./types";
import useCrmFormData from "../../hook/crm-formdata-req";
import useRequest from "../../hook/use-req";
import useCrmApi from "../../hook/crm-req";



const CustomerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.2166 17.3323C13.9349 15.9008 12.0727 15 10 15C7.92734 15 6.06492 15.9008 4.7832 17.3323M10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10C19 14.9706 14.9706 19 10 19ZM10 12C8.34315 12 7 10.6569 7 9C7 7.34315 8.34315 6 10 6C11.6569 6 13 7.34315 13 9C13 10.6569 11.6569 12 10 12Z" stroke="#4338CA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ISSUE_TYPE_OPTIONS = [
  { value: "Rewards and Payouts", label: "Rewards and Payouts" },
  { value: "Account Issues", label: "Account Issues" },
  { value: "Technical Support", label: "Technical Support" },
  { value: "General Inquiry", label: "General Inquiry" },
];

const PRIORITY_OPTIONS = [
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
  { value: "Critical", label: "Critical" },
];

const titleCasePriority = (priority?: string | null) =>
  priority ? priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase() : "High";

export function RaiseTicketModal({
  isOpen,
  onClose,
  onSubmit,
  conversationId,
  prefillCustomerData,
  existingTicket,
  onSaved,
}: RaiseTicketModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const token = localStorage.getItem("authToken");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadedFileUrls, setUploadedFileUrls] = useState<string[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [team, setTeam] = useState<TeamDetails[]>([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [draftDescription, setDraftDescription] = useState("");
  const [generatingAiMessage, setGeneratingAiMessage] = useState(false);
  // The text the description held before the last AI rewrite, so it can be undone.
  const [preAiText, setPreAiText] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<{ [key: string]: boolean }>({
    name: false,
    email: false,
    location: false,
  });

  const { makeRequest: createTicket } = useCrmFormData(`/tickets`, "POST", {
    Authorization: `Bearer ${token}`,
  });

  // Saved ticket: POST /tickets/{id}/synopsis. New ticket (no id yet):
  // POST /tickets/synopsis with the draft fields instead.
  const { makeRequest: generateSynopsis } = useCrmApi(`/tickets/${existingTicket?.id || ""}/synopsis`, "POST", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: generateNewTicketSynopsis } = useCrmApi(`/tickets/synopsis`, "POST", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: updateTicket } = useCrmApi(`/tickets/${existingTicket?.id || ""}`, "PATCH", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: getTeamList } = useRequest(`/profile/users`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: uploadMedia } = useRequest("/media/upload", "POST", {
    Authorization: `Bearer ${token}`,
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
  } = useForm<TicketFormData>({
    defaultValues: {
      name: prefillCustomerData?.name || "",
      phone: prefillCustomerData?.phone || "",
      email: prefillCustomerData?.email || "",
      location: prefillCustomerData?.state || "",
      issueType: "Rewards and Payouts",
      description: "",
      priority: "High",
      assignTo: "",
      attachments: [],
    },
  });

  const teamOptions = team.map(member => ({
    value: member.id,
    label: `${member.first_name} ${member.last_name}`.trim() || member.email
  }));

  // Upload a single file to the server
  const uploadFile = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append("files", file);

    try {
      const [response] = await uploadMedia(formData);
      if (response?.success) {
        return response.uploaded_files[0]?.preview_url;
      }
      return null;
    } catch (error) {
      console.error("Error uploading file:", error);
      return null;
    }
  };

  // Handle file selection and upload immediately
  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files) return;
    const incoming = Array.from(files);

    // Upload each file immediately
    for (const file of incoming) {
      setUploadingFiles(prev => ({ ...prev, [file.name]: true }));

      const uploadedUrl = await uploadFile(file);

      if (uploadedUrl) {
        setUploadedFileUrls(prev => [...prev, uploadedUrl]);
        setAttachments(prev => [...prev, file]);
        toast.success(`${file.name} uploaded successfully`);
      } else {
        toast.error(`Failed to upload ${file.name}`);
      }

      setUploadingFiles(prev => ({ ...prev, [file.name]: false }));
    }
  }, []);

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setUploadedFileUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const startEditing = (field: string) => {
    setEditingField(prev => ({ ...prev, [field]: true }));
  };

  const stopEditing = (field: string) => {
    setEditingField(prev => ({ ...prev, [field]: false }));
  };

  // Saved ticket → summarises it from its own fields, comments and linked
  // conversation. New ticket → "refine": whatever the agent has typed in the
  // description box is sent as `details` (along with the other form fields)
  // and comes back rewritten into a clean summary, like a "help me write"
  // helper. Undo restores what they'd typed.
  const handleGenerateAiMessage = async () => {
    const draft = (getValues("description") || "").trim();
    if (!existingTicket?.id && !draft) {
      toast.info("Type a rough note about the issue first, then refine it with AI.");
      return;
    }
    setGeneratingAiMessage(true);
    try {
      let result;
      if (existingTicket?.id) {
        result = await generateSynopsis({ model: SYNOPSIS_MODEL });
      } else {
        const v = getValues();
        result = await generateNewTicketSynopsis({
          details: draft,
          customer_name: v.name,
          issue_type: v.issueType,
          issue_description: draft.split("\n")[0].slice(0, 120),
          priority: v.priority?.toLowerCase(),
          model: SYNOPSIS_MODEL,
        });
      }
      const [response, status] = result;
      const updated = response?.data || response;
      if (status >= 200 && status < 300 && typeof updated?.synopsis === "string") {
        setPreAiText(getValues("description") || "");
        setValue("description", updated.synopsis, { shouldDirty: true });
        toast.success(existingTicket?.id ? "Synopsis generated" : "Refined with AI");
      } else {
        const detail = response?.detail;
        toast.error(typeof detail === "string" ? detail : "Failed to generate synopsis");
      }
    } catch {
      toast.error("Failed to generate synopsis");
    } finally {
      setGeneratingAiMessage(false);
    }
  };

  const undoAiMessage = () => {
    if (preAiText === null) return;
    setValue("description", preAiText, { shouldDirty: true });
    setPreAiText(null);
  };

  const onSubmitTicket = async (data: TicketFormData) => {
    setIsSubmitting(true);

    // Create payload as JSON (not FormData)
    const payload: any = {
      name: data.name,
      phone: data.phone,
      email: data.email,
      location: data.location,
      issue_type: data.issueType,
      description: data.description,
      priority: data.priority.toLowerCase(),
      assigned_to_user_id: data.assignTo,
      attachment_file_ids: uploadedFileUrls, // Send uploaded file URLs
    };

    if (conversationId) {
      payload.conversation_id = conversationId;
    }

    try {
      const request = existingTicket?.id ? updateTicket : createTicket;
      const [responseData, status] = await request(payload);

      if (status === 200 || status === 201) {
        const savedTicket = responseData?.data || responseData;
        toast.success(responseData.message || (existingTicket?.id ? "Ticket updated successfully!" : "Ticket created successfully!"));
        reset();
        setPreAiText(null);
        setAttachments([]);
        setUploadedFileUrls([]);
        onSubmit?.(data);
        if (savedTicket?.id) onSaved?.(savedTicket);
        onClose();
      } else {
        const message = responseData.detail
          ? (Array.isArray(responseData.detail)
            ? responseData.detail.map((err: any) => err.msg).join(", ")
            : responseData.detail)
          : responseData.message || "Failed to create ticket";
        toast.error(message);
      }
    } catch (error) {
      console.error("Error saving ticket:", error);
      toast.error("Failed to save ticket. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const previewUrl = (file: File) => URL.createObjectURL(file);
  const isExistingTicket = !!existingTicket?.id;
  const isAssigned = !!existingTicket?.assigned_to_user_id;

  useEffect(() => {
    if (prefillCustomerData) {
      setValue("name", existingTicket?.customer_name || prefillCustomerData.name || "");
      setValue("phone", existingTicket?.customer_phone || prefillCustomerData.phone || "");
      setValue("email", existingTicket?.customer_email || prefillCustomerData.email || "");
      setValue("location", (existingTicket as any)?.customer_location || prefillCustomerData.location || "");
    }
    if (existingTicket) {
      setValue("issueType", existingTicket.issue_type || "Rewards and Payouts");
      setValue("description", existingTicket.issue_description || "");
      setValue("priority", titleCasePriority(existingTicket.priority));
      setValue("assignTo", existingTicket.assigned_to_user_id || "");
      setValue("location", (existingTicket as any)?.customer_location || "");
      // Load existing attachments if any
      if (existingTicket.attachment_file_ids) {
        setUploadedFileUrls(existingTicket.attachment_file_ids);
      }
    }
  }, [existingTicket, prefillCustomerData, setValue]);

  useEffect(() => {
    const fetchTeam = async () => {
      setIsLoadingTeam(true);
      const [response] = await getTeamList();
      if (response && Array.isArray(response)) {
        setTeam(response);
      } else if (response && response?.users && Array.isArray(response?.users)) {
        setTeam(response?.users);
      }
      setIsLoadingTeam(false);
    };
    if (isOpen) {
      fetchTeam();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      reset({
        name: existingTicket?.customer_name || prefillCustomerData?.name || "",
        phone: existingTicket?.customer_phone || prefillCustomerData?.phone || "",
        email: existingTicket?.customer_email || prefillCustomerData?.email || "",
        location:
          (existingTicket as any)?.customer_location ||
          prefillCustomerData?.location ||
          "",
        issueType: existingTicket?.issue_type || "Rewards and Payouts",

        description:
          existingTicket?.issue_description ||
          draftDescription ||
          "",

        priority: titleCasePriority(existingTicket?.priority),
        assignTo: existingTicket?.assigned_to_user_id || "",
        attachments: [],
      });

      setAttachments([]);
      setUploadedFileUrls(existingTicket?.attachment_file_ids || []);
      setEditingField({ name: false, email: false, location: false });
    }
  }, [
    existingTicket,
    isOpen,
    reset,
    prefillCustomerData,

  ]);
  const EditableField = ({
    field,
    label,
    value,
    onChange,
    error,
    required = false,
  }: {
    field: string;
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    error?: string;
    required?: boolean;
  }) => {
    const isEditing = editingField[field];
    const shouldLock = isExistingTicket && isAssigned && !!value && !isEditing;

    return (
      <div className="relative">
        <Input
          label={label}
          value={value}
          onChange={onChange}
          error={error}
          required={required}
          readOnly={shouldLock}
          className={shouldLock ? "pr-10" : ""}
        />
        {shouldLock && (
          <button
            type="button"
            onClick={() => startEditing(field)}
            className="absolute right-3 top-[38px] text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <Edit2 size={16} />
          </button>
        )}
        {isEditing && (
          <button
            type="button"
            onClick={() => stopEditing(field)}
            className="absolute right-3 top-[38px] text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
          >
            <Check size={16} />
          </button>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  const isUploading = Object.values(uploadingFiles).some(status => status === true);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden">
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{existingTicket?.id ? "Ticket Details" : "Assign a Ticket"}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {existingTicket?.id
                ? `Ticket #${existingTicket.id.slice(0, 8)}`
                : "Add details about this conversation and assign a ticket"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 mt-0.5 p-1.5 rounded-lg text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmitTicket)} className="flex flex-col flex-1 overflow-hidden">
          <div className="overflow-y-auto px-6 py-5 flex flex-col gap-5">
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <CustomerIcon />
                <span className="text-sm font-semibold text-primary-700 dark:text-primary-400">Customer Details</span>
              </div>
              <div className="flex flex-col gap-3">
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: "Name is required" }}
                  render={({ field, fieldState }) => (
                    <EditableField
                      field="name"
                      label="Name"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState?.error?.message}

                    />
                  )}
                />

                <Controller
                  name="phone"
                  control={control}
                  rules={{ required: "Phone number is required" }}
                  render={({ field, fieldState }) => (
                    <Input
                      label="Phone / WhatsApp"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState?.error?.message}
                      readOnly={
                        isExistingTicket &&
                        isAssigned &&
                        !!field.value
                      }
                    />
                  )}
                />

                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address"
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <EditableField
                      field="email"
                      label="Email"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState?.error?.message}
                    // required
                    />
                  )}
                />

                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <EditableField
                      field="location"
                      label="Location"
                      value={field.value || ""}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
            </div>

            <div>
              <div className="flex flex-col gap-3">
                <Controller
                  name="issueType"
                  control={control}
                  rules={{ required: "Issue type is required" }}
                  render={({ field, fieldState }) => (
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Issue Type</label>
                      <Select
                        options={ISSUE_TYPE_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        error={fieldState?.error?.message}
                        placeholder="Select issue type"
                      />
                    </div>
                  )}
                />

                <Controller
                  name="description"
                  control={control}
                  rules={{ required: "Description is required" }}
                  render={({ field, fieldState }) => (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Describe the Issue</label>
                        <div className="flex items-center gap-3">
                          {preAiText !== null && (
                            <button
                              type="button"
                              onClick={undoAiMessage}
                              className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline underline-offset-2"
                            >
                              Undo
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={handleGenerateAiMessage}
                            disabled={generatingAiMessage}
                            className="flex items-center gap-1 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 disabled:opacity-60 transition-colors"
                          >
                            {generatingAiMessage ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                            {isExistingTicket ? "Generate with AI" : "Refine with AI"}
                          </button>
                        </div>
                      </div>
                      <div className="relative">
                        <textarea
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e);
                            setDraftDescription(e.target.value);
                          }}
                          maxLength={400}
                          rows={4}
                          placeholder={isExistingTicket ? "Type issue description" : "Type a rough note about the issue, then click Refine with AI to tidy it up"}
                          className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 resize-none focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-500/20 transition-all"
                        />
                        <span className="absolute bottom-2.5 right-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">
                          {field.value?.length || 0}/400
                        </span>
                      </div>
                      {fieldState?.error && (
                        <span className="text-xs text-red-500 dark:text-red-400">{fieldState.error.message}</span>
                      )}
                    </div>
                  )}
                />

                {/* Attachments with upload status */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Attachments (Optional)</label>
                  <div
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="flex flex-wrap items-center gap-2"
                  >
                    {attachments.map((file, i) => (
                      <div key={i} className="relative group w-14 h-14 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shrink-0">
                        {file.type.startsWith("image/") ? (
                          <img src={previewUrl(file)} alt={file.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-primary-50 dark:bg-primary-500/15 flex items-center justify-center text-primary-600 dark:text-primary-400 text-[10px] font-medium text-center px-1">
                            {file.name.split(".").pop()?.toUpperCase()}
                          </div>
                        )}
                        {uploadingFiles[file.name] && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <Loader2 className="w-4 h-4 text-white animate-spin" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeAttachment(i)}
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                          disabled={uploadingFiles[file.name]}
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </div>
                    ))}

                    {attachments.length < 3 && !isUploading && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 underline underline-offset-2 transition-colors"
                      >
                        Upload Files
                      </button>
                    )}
                    {isUploading && (
                      <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Uploading...</span>
                    )}
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                  <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">
                    Upload up to 3 files (PNG, JPG, PDF, or DOC), max 10 MB total.
                  </p>
                </div>

                <Controller
                  name="priority"
                  control={control}
                  rules={{ required: "Priority is required" }}
                  render={({ field, fieldState }) => (
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Priority</label>
                      <Select
                        options={PRIORITY_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        error={fieldState?.error?.message}
                        placeholder="Select priority"
                      />
                    </div>
                  )}
                />

                <Controller
                  name="assignTo"
                  control={control}
                  rules={{ required: "Please select a team member to assign this ticket" }}
                  render={({ field, fieldState }) => (
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                        Assign to <span className="text-red-500 dark:text-red-400">*</span>
                      </label>
                      <Select
                        options={teamOptions}
                        value={field.value}
                        onChange={field.onChange}
                        error={fieldState?.error?.message}
                        placeholder={isLoadingTeam ? "Loading team members..." : "Select team member"}
                      />
                      {!isLoadingTeam && teamOptions.length === 0 && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          No team members found. Please add team members first.
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>
          </div>

          <div className="px-6 pb-5 pt-3 flex flex-col gap-2 border-t border-gray-100 dark:border-gray-800">
            <button
              type="submit"
              disabled={isSubmitting || isLoadingTeam || teamOptions.length === 0 || isUploading}
              className="w-full py-3 rounded-xl bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : existingTicket?.id ? "Update Ticket" : "Submit Ticket"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}